// Stage Mail-V2.5 (2026-08-19): メールプロバイダー プリセット。
//
// 目的:
//   - 一般ユーザーが IMAP/SMTP ホスト名・ポート・セキュリティ を手動で調べなくて済む
//   - Gmail / Yahoo! JAPAN / Outlook / iCloud は1回選択で 自動入力
//   - その他(企业邮箱等) は host/port/security を ユーザーが引き続き手動入力
//
// 配置: src/api/ 共有層 — Settings と Mail V2 の共有 MailAccountForm から利用。
// (modules/mail-v2/lib/ に置くと V1 form が V2 に依存してしまうため不可)
//
// 後端 detect_provider はこのファイルと独立 — preset で host を 事前入力 するので
// 後端は受信した host 文字列から provider を 動的検出 する (mailbox_profile.detect_provider)。

export type ProviderSecurity = "ssl" | "starttls" | "none";

export type ProviderPreset = {
  imap: {
    host: string;
    port: number;
    security: ProviderSecurity;
  };
  smtp: {
    host: string;
    port: number;
    security: ProviderSecurity;
  };
};

export type ProviderKey = "gmail" | "yahoo_jp" | "outlook" | "icloud" | "other";

export const PROVIDER_PRESETS: Record<ProviderKey, ProviderPreset | null> = {
  gmail: {
    imap: { host: "imap.gmail.com", port: 993, security: "ssl" },
    smtp: { host: "smtp.gmail.com", port: 587, security: "starttls" },
  },
  yahoo_jp: {
    imap: { host: "imap.mail.yahoo.co.jp", port: 993, security: "ssl" },
    smtp: { host: "smtp.mail.yahoo.co.jp", port: 465, security: "ssl" },
  },
  outlook: {
    imap: { host: "outlook.office365.com", port: 993, security: "ssl" },
    smtp: { host: "smtp.office365.com", port: 587, security: "starttls" },
  },
  icloud: {
    imap: { host: "imap.mail.me.com", port: 993, security: "ssl" },
    smtp: { host: "smtp.mail.me.com", port: 587, security: "starttls" },
  },
  // その他 は null — host/port/security を ユーザー が引き続き手動入力
  other: null,
};

export const PROVIDER_LABELS: Record<ProviderKey, string> = {
  gmail: "Gmail",
  yahoo_jp: "Yahoo! JAPAN",
  outlook: "Outlook / Microsoft 365",
  icloud: "iCloud",
  other: "その他（手動入力）",
};

export const PROVIDER_ORDER: ProviderKey[] = [
  "gmail",
  "yahoo_jp",
  "outlook",
  "icloud",
  "other",
];

export function detectProviderFromEmail(email: string): ProviderKey {
  const domain = email.trim().toLowerCase().split("@").pop() ?? "";
  if (domain === "gmail.com" || domain === "googlemail.com") return "gmail";
  if (domain === "yahoo.co.jp" || domain.endsWith(".yahoo.co.jp")) return "yahoo_jp";
  if (
    domain === "outlook.com" ||
    domain === "hotmail.com" ||
    domain === "live.com" ||
    domain === "office365.com"
  ) {
    return "outlook";
  }
  if (domain === "icloud.com" || domain === "me.com" || domain === "mac.com") return "icloud";
  return "other";
}

/** Apply preset to form state. Mutates by returning new values; caller should
 *  setState them all at once. Returns null when key is "other" (caller keeps
 *  whatever the user already typed).
 */
export function presetToFormValues(
  key: ProviderKey,
): Pick<ProviderPreset, "imap" | "smtp"> | null {
  const p = PROVIDER_PRESETS[key];
  if (!p) return null;
  return { imap: p.imap, smtp: p.smtp };
}
